import React, { useContext, useEffect, useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Dimensions,
  PixelRatio,
  Platform,
  ActivityIndicator,
  TextInput,
  Modal,
} from "react-native";
import { MaterialCommunityIcons, Ionicons, FontAwesome } from "@expo/vector-icons";
import AppleSignin from "react-apple-signin-auth";
import Colours from "../../utils/Colours";
import AppContext from "../../context/AppContext";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import ErrorAlertComp from "../../alertComp/ErrorAlertComp";
import { useGoogleAuth } from "../../hooks/useGoogleAuth";
import { useAppleAuth } from "../../hooks/useAppleAuth";
import { jwtDecode } from "jwt-decode";

import useAuth from "../../hooks/useAuth";
import { clearAwaitingAck } from "../../utils/guestConversionAck";

// ── RESPONSIVE UTILITIES ──
const { width, height } = Dimensions.get("window");
const BASE_WIDTH = 375;
const BASE_HEIGHT = 812;

const dynamicFontSize = (size) => {
  const scaleFactor = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT);
  const newSize = size * scaleFactor;
  return Math.round(PixelRatio.roundToNearestPixel(newSize));
};

const dynamicSpacing = (size) => (width / BASE_WIDTH) * size;
const dynamicSize = (size) => (width / BASE_WIDTH) * size;

const GuestConversionScreen = ({ navigation }) => {
  const { auth, setAuth } = useContext(AppContext);
  const { email, phoneNumber } = useAuth();
  const [isLoadingOTP, setIsLoadingOTP] = useState(false);
  const [whatsappNumber, setWhatsAppNumber] = useState("");
  const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
  const [isPhoneErrorVisible, setIsPhoneErrorVisible] = useState(false);

  const [tableName, setTableName] = useState("");
  const [loadingTable, setLoadingTable] = useState(true);
  const [pendingGoogleToken, setPendingGoogleToken] = useState(null);
  const [pendingAppleToken, setPendingAppleToken] = useState(null);

  const axiosPrivate = useAxiosPrivate();

  // ── GOOGLE AUTH HOOK ──
  const {
    promptAsync: promptGoogleAsync,
    request: googleRequest,
    isAuthLoading: isGoogleLoading,
    errorMessage: googleErrorMessage,
    isErrorCompVisible: isGoogleErrorVisible,
    setIsErrorCompVisible: setIsGoogleErrorVisible,
    // } = useGoogleAuth((strictLocalToken) => {
    //   // setAuth({ accessToken: strictLocalToken, isProfileComplete: true });
    //   // setSignupCompleted(true);
    //   setPendingGoogleToken(strictLocalToken);
    // });
  } = useGoogleAuth((strictLocalToken) => {
    let decodedFields = {};
    try {
      const decoded = jwtDecode(strictLocalToken);
      const userInfoToken = decoded?.UserInfo;
      if (userInfoToken) {
        decodedFields = {
          email: userInfoToken?.email,
          phoneNumber: userInfoToken?.phoneNumber,
          isGuest: userInfoToken?.isGuest ?? false,
        };
      }
    } catch (err) {
      console.error("Failed to decode Google accessToken:", err);
    }

    // Commit immediately — backend may have migrated/deleted the old guest user.
    setAuth((prev) => ({
      ...prev,
      accessToken: strictLocalToken,
      isProfileComplete: true,
      ...decodedFields,
    }));

    setPendingGoogleToken(strictLocalToken); // drives local UI swap to Done
  });

  // ── APPLE AUTH HOOK ──
  // ── APPLE AUTH HOOK ──
  const {
    handleAppleSuccess,
    handleAppleFailure,
    isAuthLoading: isAppleLoading,
    errorMessage: appleErrorMessage,
    isErrorCompVisible: isAppleErrorVisible,
    setIsErrorCompVisible: setIsAppleErrorVisible,
  } = useAppleAuth((strictLocalToken) => {
    let decodedFields = {};
    try {
      const decoded = jwtDecode(strictLocalToken);
      const userInfoToken = decoded?.UserInfo;
      if (userInfoToken) {
        decodedFields = {
          email: userInfoToken?.email,
          phoneNumber: userInfoToken?.phoneNumber,
          isGuest: userInfoToken?.isGuest ?? false,
        };
      }
    } catch (err) {
      console.error("Failed to decode Apple accessToken:", err);
    }

    setAuth((prev) => ({
      ...prev,
      accessToken: strictLocalToken,
      isProfileComplete: true,
      ...decodedFields,
    }));

    setPendingAppleToken(strictLocalToken);
  });
  // const {
  //   handleAppleSuccess,
  //   handleAppleFailure,
  //   isAuthLoading: isAppleLoading,
  //   errorMessage: appleErrorMessage,
  //   isErrorCompVisible: isAppleErrorVisible,
  //   setIsErrorCompVisible: setIsAppleErrorVisible,
  // } = useAppleAuth((strictLocalToken) => {
  //   setAuth({ accessToken: strictLocalToken, isProfileComplete: true });
  //   // setSignupCompleted(true);
  // });

  // Determine active error messaging states
  const activeErrorMessage = googleErrorMessage || appleErrorMessage || phoneErrorMessage;
  const isAnyErrorVisible = isGoogleErrorVisible || isAppleErrorVisible || isPhoneErrorVisible;
  const closeErrorModal = () => {
    setIsGoogleErrorVisible(false);
    setIsAppleErrorVisible(false);
    setIsPhoneErrorVisible(false);
  };

  // Check if user is fully authenticated (has valid non-guest email or phone)
  // const email = auth?.email || "";
  // const phoneNumber = auth?.phoneNumber || "";
  const isGuestEmail = email.toLowerCase().includes("@guest.local");
  // const isAuthenticated = (!!email && !isGuestEmail) || !!phoneNumber || !!pendingGoogleToken;
  const isAuthenticated =
    (!!email && !isGuestEmail) || !!phoneNumber || !!pendingGoogleToken || !!pendingAppleToken;

  console.log("email", email, "phoneNumber", phoneNumber);

  // Local editable state for the table name
  const [editableTableName, setEditableTableName] = useState(tableName);
  const [isUpdatingTable, setIsUpdatingTable] = useState(false);

  // Keep local state synced when initial tableName finishes loading
  useEffect(() => {
    if (tableName) {
      setEditableTableName(tableName);
    }
  }, [tableName]);

  // ── FETCH MOST RECENT TABLE ──
  useEffect(() => {
    let isMounted = true;

    const fetchMostRecentTable = async () => {
      try {
        setLoadingTable(true);
        const res = await axiosPrivate.get("/oneMostRecentTable/oneMostRecentTable");

        console.log("res.data", res?.data);

        if (isMounted && res?.data) {
          // Adjust payload mapping depending on exact backend property response
          const fetchedName =
            res.data?.tableName || res.data?.table?.name || res.data?.name || "Your Table";
          setTableName(fetchedName);
        }
      } catch (error) {
        console.error("Failed to fetch most recent table:", error);
        if (isMounted) setTableName("Your Table");
      } finally {
        if (isMounted) setLoadingTable(false);
      }
    };

    fetchMostRecentTable();

    return () => {
      isMounted = false;
    };
  }, [axiosPrivate, auth]);

  // Fallback to table name or default table identifier
  // const tableName = auth?.tableName || auth?.tableDetails?.name || "Table 04";

  // Auth Handlers (Connect these to your existing Google / Apple / WhatsApp auth endpoints)

  const handleGoogleSignup = () => {
    // Redirect to Google OAuth / Onboarding Flow
    navigation.navigate("signup", { provider: "google" });
  };

  const handleAppleSignup = () => {
    // Execute Apple Auth Flow
    navigation.navigate("signup", { provider: "apple" });
  };

  const handleWhatsAppSignup = () => {
    // Navigate to phone/OTP onboarding
    navigation.navigate("personal_bio", { isWhatsApp: true });
  };

  // const handleDone = async () => {
  //   try {
  //     setIsUpdatingTable(true);

  //     // 1. Update table name if modified
  //     if (editableTableName.trim() !== "" && editableTableName !== tableName) {
  //       await axiosPrivate.patch("/updateTableName/updateTableName", {
  //         tableName: editableTableName.trim(),
  //       });
  //     }

  //     // 2. Commit the pending Google auth to global auth — always, regardless of table name change
  //     if (pendingGoogleToken) {
  //       let decodedFields = {};
  //       try {
  //         const decoded = jwtDecode(pendingGoogleToken);
  //         const userInfoToken = decoded?.UserInfo;
  //         if (userInfoToken) {
  //           decodedFields = {
  //             email: userInfoToken?.email,
  //             phoneNumber: userInfoToken?.phoneNumber,
  //             isGuest: userInfoToken?.isGuest ?? false,
  //           };
  //         }
  //       } catch (err) {
  //         console.error("Failed to decode Google accessToken:", err);
  //       }

  //       setAuth((prev) => ({
  //         ...prev,
  //         accessToken: pendingGoogleToken,
  //         isProfileComplete: true,
  //         ...decodedFields,
  //       }));
  //     }

  //     // 3. Navigate to Home/Main App
  //     navigation.reset({
  //       index: 0,
  //       routes: [{ name: "main" }],
  //     });
  //   } catch (error) {
  //     console.error("Failed to update table name:", error);
  //   } finally {
  //     setIsUpdatingTable(false);
  //   }
  // };

  const handleDone = async () => {
    try {
      setIsUpdatingTable(true);

      if (editableTableName.trim() !== "" && editableTableName !== tableName) {
        await axiosPrivate.patch("/updateTableName/updateTableName", {
          tableName: editableTableName.trim(),
        });
      }

      clearAwaitingAck(); // ← only place the screen is allowed to release
      navigation.reset({ index: 0, routes: [{ name: "main" }] });
    } catch (error) {
      console.error("Failed to update table name:", error);
    } finally {
      setIsUpdatingTable(false);
    }
  };

  const handleGetOTP = () => {};

  const isAuthLoading = isGoogleLoading || isAppleLoading;

  return (
    <ScrollView
      style={styles.screenContainer}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.card}>
        {/* TOP STATUS BADGE */}
        <View style={styles.successBadge}>
          <Ionicons name="checkmark-circle" size={dynamicSize(20)} color={Colours.DARKGREEN} />
          <Text style={styles.successBadgeText}>Order Received & Confirmed!</Text>
        </View>

        {/* HERO ICON */}
        <View style={styles.iconContainer}>
          <MaterialCommunityIcons
            name="party-popper"
            size={dynamicSize(36)}
            color={Colours.PRIMARY}
          />
        </View>

        {/* HEADLINE */}
        <Text style={styles.title}>Give your table a Name!</Text>
        <Text style={styles.subtitle}>
          Your order has been sent to the bar/kitchen. Complete your profile now to get full access
          to your table features.
        </Text>

        {/* TABLE NAME SPOTLIGHT */}
        {/* <View style={styles.tableSpotlightCard}>
          <Text style={styles.tableSpotlightLabel}>CURRENT TABLE NAME</Text>
          <Text style={styles.tableNameText}>{tableName}</Text>
          <View style={styles.tableNoteRow}>
            <Ionicons name="create-outline" size={dynamicSize(14)} color={Colours.PRIMARY} />
            <Text style={styles.tableNoteText}>
              Give your table a unique name to represent you, your brand or your crew tonight!
            </Text>
          </View>
        </View> */}

        {/* TABLE NAME DISPLAY OR EDITABLE FORM FIELD */}
        <View style={styles.tableSpotlightCard}>
          <Text style={styles.tableSpotlightLabel}>CURRENT TABLE NAME</Text>

          {isAuthenticated ? (
            // ── EDITABLE FORM FIELD (SHOWN WHEN LOGGED IN) ──
            <View style={styles.editTableInputContainer}>
              <TextInput
                style={styles.editTableTextInput}
                value={editableTableName}
                onChangeText={setEditableTableName}
                placeholder="Enter your table name"
                placeholderTextColor="#999"
                maxLength={30}
              />
            </View>
          ) : (
            // ── STATIC DISPLAY (SHOWN BEFORE AUTH) ──
            <Text style={styles.tableNameText}>{tableName}</Text>
          )}

          <View style={styles.tableNoteRow}>
            <Ionicons name="create-outline" size={dynamicSize(14)} color={Colours.PRIMARY} />
            <Text style={styles.tableNoteText}>
              Give your table a unique name to represent you, your brand or your crew tonight!
              {/* {isAuthenticated
                ? "Feel free to personalize your table name before proceeding!"
                : "Give your table a unique name to represent you, your brand or your crew tonight!"} */}
            </Text>
          </View>
        </View>

        {/* VALUE PROPOSITION LIST */}
        <View style={styles.benefitsContainer}>
          <Text style={styles.benefitsHeader}>Sign up with Meeza to:</Text>

          <View style={styles.benefitRow}>
            <View style={styles.benefitIconWrapper}>
              <Ionicons name="time-outline" size={dynamicSize(18)} color={Colours.PRIMARY} />
            </View>
            <View style={styles.benefitTextContainer}>
              <Text style={styles.benefitTitle}>Track Your Order Live</Text>
              <Text style={styles.benefitSubtext}>
                Get real-time updates as your drinks and meals are prepared.
              </Text>
            </View>
          </View>

          <View style={styles.benefitRow}>
            <View style={styles.benefitIconWrapper}>
              <Ionicons name="sparkles-outline" size={dynamicSize(18)} color={Colours.PRIMARY} />
            </View>
            <View style={styles.benefitTextContainer}>
              <Text style={styles.benefitTitle}>Give Your Table a Unique Name</Text>
              <Text style={styles.benefitSubtext}>
                Personalize your table screen and stand out in the venue.
              </Text>
            </View>
          </View>

          <View style={styles.benefitRow}>
            <View style={styles.benefitIconWrapper}>
              <Ionicons name="trophy-outline" size={dynamicSize(18)} color={Colours.PRIMARY} />
            </View>
            <View style={styles.benefitTextContainer}>
              <Text style={styles.benefitTitle}>Doings Trend Leaderboard</Text>
              <Text style={styles.benefitSubtext}>
                See how your table ranks tonight on the live trend!
              </Text>
            </View>
          </View>
        </View>

        {/* CONDITIONAL BODY CONTENT */}
        {isAuthenticated ? (
          // ── DONE BUTTON (SHOWN WHEN LOGGED IN) ──
          <View style={styles.authenticatedActionsContainer}>
            <TouchableOpacity
              style={styles.doneButton}
              activeOpacity={0.8}
              disabled={isUpdatingTable}
              onPress={handleDone}
            >
              {isUpdatingTable ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.doneButtonText}>Done</Text>
              )}
            </TouchableOpacity>
          </View>
        ) : isAuthLoading ? (
          // ── LOADING INDICATOR ──
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="small" color={Colours.PRIMARY} />
            <Text style={styles.loadingText}>Authenticating...</Text>
          </View>
        ) : (
          <>
            {/* SIGNUP METHODS */}
            <Text style={styles.signupActionTitle}>Choose how to continue</Text>

            {/* GOOGLE SIGNUP */}
            <TouchableOpacity
              style={styles.googleButton}
              disabled={!googleRequest || isAuthLoading}
              // onPress={() => promptAsync()}
              onPress={() => promptGoogleAsync()}
            >
              <FontAwesome
                name="google"
                size={dynamicSize(18)}
                color={Colours.PRIMARY}
                style={styles.buttonIcon}
              />
              <Text style={styles.googleButtonText}>Continue with Google</Text>
            </TouchableOpacity>

            {/* APPLE SIGNUP (Shown on iOS or Web) */}
            <AppleSignin
              authOptions={{
                // clientId: "com.mexa.pwa", // Your registered Apple Services
                clientId: "com.mexxzia.pwa",
                scope: "email name",
                redirectURI: "https://app.getmeeza.com/",
                usePopup: true, // Prevents page reload so cart state remains intact
              }}
              onSuccess={handleAppleSuccess}
              onError={handleAppleFailure}
              render={(props) => (
                <TouchableOpacity
                  style={styles.appleButton}
                  activeOpacity={0.8}
                  disabled={isAuthLoading}
                  onPress={props.onClick}
                >
                  <FontAwesome
                    name="apple"
                    size={dynamicSize(20)}
                    color="#FFFFFF"
                    style={styles.buttonIcon}
                  />
                  <Text style={styles.appleButtonText}>Continue with Apple</Text>
                </TouchableOpacity>
              )}
            />

            {/* WHATSAPP SIGNUP */}
            {/* <TouchableOpacity style={styles.whatsappButton} onPress={handleWhatsAppSignup}>
          <FontAwesome
            name="whatsapp"
            size={dynamicSize(20)}
            color="#FFFFFF"
            style={styles.buttonIcon}
          />
          <Text style={styles.whatsappButtonText}>Sign up with WhatsApp</Text>
        </TouchableOpacity> */}
            <View style={styles.dividerRow}>
              <View style={styles.dividerLine} />
              <Text style={styles.dividerText}>OR WITH WHATSAPP</Text>
              <View style={styles.dividerLine} />
            </View>

            <View style={styles.whatsappInputWrapper}>
              <View style={styles.phoneInputRow}>
                <View style={styles.countryCodeBox}>
                  <Text style={styles.countryCodeTxt}>+234</Text>
                </View>
                <TextInput
                  style={styles.whatsappTextInput}
                  placeholder="WhatsApp Phone Number"
                  placeholderTextColor="#999"
                  keyboardType="phone-pad"
                  value={whatsappNumber}
                  onChangeText={setWhatsAppNumber}
                />
              </View>
              <TouchableOpacity
                disabled={isLoadingOTP}
                style={[styles.getOtpButton]}
                activeOpacity={0.8}
                onPress={handleGetOTP}
              >
                {isLoadingOTP ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  <Text style={styles.getOtpButtonText}>Get OTP</Text>
                )}
              </TouchableOpacity>
            </View>
          </>
        )}
      </View>
      <Modal visible={isAnyErrorVisible} transparent={true} animationType="fade">
        <ErrorAlertComp message={activeErrorMessage} onClose={closeErrorModal} />
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  screenContainer: {
    flex: 1,
    backgroundColor: Colours.LIGHTEST,
  },
  loaderContainer: {
    paddingVertical: dynamicSpacing(20),
    alignItems: "center",
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: dynamicSpacing(16),
    paddingVertical: dynamicSpacing(24),
  },
  card: {
    width: "100%",
    maxWidth: dynamicSize(420),
    backgroundColor: "#FFFFFF",
    borderRadius: dynamicSize(24),
    padding: dynamicSpacing(20),
    alignItems: "center",
    elevation: 4,
    shadowColor: Colours.BLACK,
    shadowOffset: { width: 0, height: dynamicSize(4) },
    shadowOpacity: 0.08,
    shadowRadius: dynamicSize(12),
  },
  successBadge: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: Colours.LIGHTESTGREEN,
    paddingHorizontal: dynamicSpacing(12),
    paddingVertical: dynamicSpacing(6),
    borderRadius: dynamicSize(20),
    marginBottom: dynamicSpacing(16),
  },
  successBadgeText: {
    fontSize: dynamicFontSize(12),
    fontFamily: "Sen-Bold",
    color: Colours.DARKGREEN,
    marginLeft: dynamicSpacing(6),
  },
  iconContainer: {
    width: dynamicSize(68),
    height: dynamicSize(68),
    borderRadius: dynamicSize(34),
    backgroundColor: Colours.PRIMARY_LIGHTER,
    justifyContent: "center",
    alignItems: "center",
    marginBottom: dynamicSpacing(14),
  },
  title: {
    fontSize: dynamicFontSize(22),
    fontFamily: "Sen-Bold",
    color: Colours.DARKGRAY,
    textAlign: "center",
    marginBottom: dynamicSpacing(6),
  },
  subtitle: {
    fontSize: dynamicFontSize(13),
    fontFamily: "Sen-Regular",
    color: Colours.DARKLIGHTGRAY,
    textAlign: "center",
    lineHeight: dynamicFontSize(18),
    marginBottom: dynamicSpacing(18),
    paddingHorizontal: dynamicSpacing(8),
  },
  tableSpotlightCard: {
    width: "100%",
    backgroundColor: Colours.LIGHTEST,
    borderWidth: 1,
    borderColor: Colours.PRIMARY_LIGHT,
    borderRadius: dynamicSize(16),
    padding: dynamicSpacing(14),
    alignItems: "center",
    marginBottom: dynamicSpacing(18),
  },
  tableSpotlightLabel: {
    fontSize: dynamicFontSize(10),
    fontFamily: "Sen-Bold",
    color: Colours.DARKLIGHTGRAY,
    letterSpacing: 1,
    marginBottom: dynamicSpacing(2),
  },
  tableNameText: {
    fontSize: dynamicFontSize(22),
    fontFamily: "Sen-Bold",
    color: Colours.PRIMARY,
    marginBottom: dynamicSpacing(6),
  },
  tableNoteRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: dynamicSpacing(4),
  },
  tableNoteText: {
    fontSize: dynamicFontSize(11),
    fontFamily: "Sen-Regular",
    color: Colours.DARKGRAY,
    marginLeft: dynamicSpacing(4),
    textAlign: "center",
  },
  benefitsContainer: {
    width: "100%",
    backgroundColor: Colours.LIGHTEST,
    borderRadius: dynamicSize(16),
    padding: dynamicSpacing(14),
    marginBottom: dynamicSpacing(20),
  },
  benefitsHeader: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Bold",
    color: Colours.DARKGRAY,
    marginBottom: dynamicSpacing(12),
  },
  benefitRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: dynamicSpacing(12),
  },
  benefitIconWrapper: {
    width: dynamicSize(32),
    height: dynamicSize(32),
    borderRadius: dynamicSize(16),
    backgroundColor: Colours.PRIMARY_LIGHTER,
    justifyContent: "center",
    alignItems: "center",
    marginRight: dynamicSpacing(10),
    marginTop: dynamicSpacing(2),
  },
  benefitTextContainer: {
    flex: 1,
  },
  benefitTitle: {
    fontSize: dynamicFontSize(13),
    fontFamily: "Sen-Bold",
    color: Colours.DARKGRAY,
  },
  benefitSubtext: {
    fontSize: dynamicFontSize(11),
    fontFamily: "Sen-Regular",
    color: Colours.DARKLIGHTGRAY,
    lineHeight: dynamicFontSize(15),
    marginTop: dynamicSpacing(2),
  },
  signupActionTitle: {
    fontSize: dynamicFontSize(13),
    fontFamily: "Sen-Bold",
    color: Colours.DARKGRAY,
    marginBottom: dynamicSpacing(12),
    textAlign: "center",
  },
  googleButton: {
    flexDirection: "row",
    width: "100%",
    height: dynamicSize(48),
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: Colours.MIDGRAY,
    borderRadius: dynamicSize(12),
    justifyContent: "center",
    alignItems: "center",
    marginBottom: dynamicSpacing(10),
  },
  googleButtonText: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Bold",
    color: Colours.DARKGRAY,
  },
  appleButton: {
    flexDirection: "row",
    width: "100%",
    height: dynamicSize(48),
    backgroundColor: Colours.BLACK,
    borderRadius: dynamicSize(12),
    justifyContent: "center",
    alignItems: "center",
    marginBottom: dynamicSpacing(10),
  },
  appleButtonText: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Bold",
    color: "#FFFFFF",
  },
  whatsappButton: {
    flexDirection: "row",
    width: "100%",
    height: dynamicSize(48),
    backgroundColor: Colours.DARKGREEN,
    borderRadius: dynamicSize(12),
    justifyContent: "center",
    alignItems: "center",
    marginBottom: dynamicSpacing(8),
  },
  whatsappButtonText: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Bold",
    color: "#FFFFFF",
  },
  buttonIcon: {
    marginRight: dynamicSpacing(10),
  },

  dividerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: dynamicSpacing(16),
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: "#EAEAEF",
  },
  dividerText: {
    fontSize: dynamicFontSize(10),
    fontFamily: "Sen-Bold",
    color: "#888",
    paddingHorizontal: dynamicSpacing(10),
    letterSpacing: 0.5,
  },
  whatsappInputWrapper: {
    flex: 1,
    width: "100%",
    gap: dynamicSpacing(10),
    // backgroundColor: "yellow",
  },
  phoneInputRow: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#EAEAEF",
    borderRadius: dynamicSize(12),
    overflow: "hidden",
    backgroundColor: "#FAFAFC",
    height: dynamicSize(50),
    alignItems: "center",
  },
  countryCodeBox: {
    paddingHorizontal: dynamicSpacing(14),
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F0F0F5",
    height: "100%",
    borderRightWidth: 1,
    borderRightColor: "#EAEAEF",
  },
  countryCodeTxt: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Bold",
    color: "#333",
  },
  whatsappTextInput: {
    flex: 1,
    // paddingHorizontal: dynamicSpacing(12),
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Regular",
    color: "#1A1A1A",
    height: "100%",
  },
  getOtpButton: {
    height: dynamicSize(50),
    borderRadius: dynamicSize(12),
    backgroundColor: Colours.PRIMARY || "#2A65F0",
    justifyContent: "center",
    alignItems: "center",
  },
  getOtpButtonText: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Bold",
    color: "#FFF",
  },

  editTableInputContainer: {
    marginVertical: dynamicSize(10),
    backgroundColor: "#F5F5F5",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colours.PRIMARY,
    paddingHorizontal: dynamicSize(12),
  },
  editTableTextInput: {
    height: dynamicSize(44),
    fontSize: dynamicSize(16),
    fontWeight: "600",
    color: "#333333",
  },
  authenticatedActionsContainer: {
    marginTop: dynamicSize(24),
    width: "100%",
  },
  doneButton: {
    backgroundColor: Colours.PRIMARY,
    height: dynamicSize(48),
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  doneButtonText: {
    color: "#FFFFFF",
    fontSize: dynamicSize(16),
    fontWeight: "bold",
  },
});

export default GuestConversionScreen;
